import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { AudioPicker } from "@/features/audio-picker";
import { StageRulesField } from "@/features/stage-rules";
import { ApiError } from "@/lib/api";
import { RINGBACK_US, isTone } from "@/lib/audio";
import {
  LANGUAGE_ROUTING, LINE_BOUNDS, LINE_PURPOSES, NEW_LINE, RING_STRATEGIES, SKILL_STRATEGIES,
  formatSpan, languageRoutingText, lineChanges, linePurposeText, lineValues, mediaProblem,
  purposeTaken, ringStrategyText, skillStrategyText, type LineValues,
} from "@/lib/campaign";
import { useFormChanged, zodResolver } from "@/lib/form";
import { describeStageRule, stageRuleProblem } from "@/lib/stage-rules";
import {
  useAddLine, useDeleteLine, useUpdateCampaign, useUpdateLine, type LineSettingsInput,
} from "@/lib/queries";
import type { Campaign, Queue } from "@/lib/types";

/**
 * Where the campaign's calls wait for a rep, and how long each step may take (ADR-014).
 *
 * A campaign's lines are its own. The primary one comes and goes with the campaign; a second is
 * added only when some calls need different settings — the closers a setter hands off to, or an
 * inbound number with its own ring time. Every line, the primary included, is edited through
 * `PUT /queues/{id}`; the campaign's `routing` field writes the same row, so there is one editor
 * for both rather than two that can disagree.
 */
export function CampaignLines({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const remove = useDeleteLine();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Queue | undefined>();
  const [deleting, setDeleting] = React.useState<Queue | null>(null);

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader
          title="Lines"
          description="Where the campaign's calls wait for a rep, and how long each step may take."
          action={
            canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(undefined);
                  setDialogOpen(true);
                }}
              >
                <Plus />
                Add a line
              </Button>
            )
          }
        />
        {campaign.lines.length === 0 ? (
          <CardBody>
            <Callout tone="warning">This campaign has no line, so its calls have nowhere to wait.</Callout>
          </CardBody>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Line</TableHead>
                <TableHead>Rings</TableHead>
                <TableHead>Rings a rep for</TableHead>
                <TableHead>Wrap-up</TableHead>
                <TableHead>Disposition window</TableHead>
                <TableHead>Caller waits up to</TableHead>
                <TableHead>Reps</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaign.lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell>
                    <span className="flex flex-col gap-0.5">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-mono text-xs text-text">{line.name}</span>
                        <Badge tone={line.purpose === "primary" ? "accent" : "neutral"} size="sm">
                          {linePurposeText(line.purpose).label}
                        </Badge>
                        {!line.enabled && (
                          <Badge tone="warning" size="sm">
                            Off
                          </Badge>
                        )}
                      </span>
                      {line.description && (
                        <span className="text-xs text-text-muted">{line.description}</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-col gap-0.5">
                      <span>{ringStrategyText(line.ring_strategy).label}</span>
                      {line.ring_strategy !== "ring_all" && (
                        <span className="text-xs text-text-muted">
                          {skillStrategyText(line.skill_strategy).label}
                        </span>
                      )}
                      {line.previous_rep_first && (
                        <span className="text-xs text-text-muted">Previous rep first</span>
                      )}
                      {(line.stage_rules ?? []).length > 0 && (
                        <span
                          className="text-xs text-text-muted"
                          title={line.stage_rules.map((r) => describeStageRule(r)).join("\n")}
                        >
                          {line.stage_rules.length === 1
                            ? "1 stage rule"
                            : `${line.stage_rules.length} stage rules`}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatSpan(line.ring_timeout_seconds)}</TableCell>
                  <TableCell className="tabular-nums">
                    {line.wrap_up_seconds > 0 ? formatSpan(line.wrap_up_seconds) : "None"}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {line.disposition_seconds > 0 ? formatSpan(line.disposition_seconds) : "None"}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {line.max_wait_seconds > 0 ? formatSpan(line.max_wait_seconds) : "No limit"}
                  </TableCell>
                  <TableCell>
                    <Badge tone={line.member_count > 0 ? "neutral" : "warning"}>{line.member_count}</Badge>
                  </TableCell>
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${line.name}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditing(line);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil />
                            Edit settings
                          </DropdownMenuItem>
                          {line.purpose !== "primary" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem destructive onSelect={() => setDeleting(line)}>
                                <Trash2 />
                                Delete line
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <LanguageRouting campaign={campaign} canManage={canManage} />

      <LineDialog open={dialogOpen} onOpenChange={setDialogOpen} campaign={campaign} line={editing} />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete this line?"
        description={
          <>
            <span className="font-mono">{deleting?.name}</span> and its staffing will be removed.
            Numbers routed to it, and reps signed in to it, block this.
          </>
        }
        confirmLabel="Delete line"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("Line deleted", deleting.name);
            setDeleting(null);
          } catch (e) {
            toast.error("Could not delete the line", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </div>
  );
}

/** A campaign setting, saved as soon as it is picked: one control, nothing to batch it with. */
function LanguageRouting({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();

  return (
    <Card>
      <CardHeader
        title="Language"
        description="Whether a lead's language decides which rep takes their call. A rep's languages are their skills."
      />
      <CardBody>
        <Field
          label="Language routing"
          htmlFor="campaign-language-routing"
          hint={languageRoutingText(campaign.language_routing).description}
        >
          <Select
            value={campaign.language_routing}
            disabled={!canManage || update.isPending}
            onValueChange={(next) =>
              update.mutate(
                { id: campaign.id, language_routing: next },
                {
                  onSuccess: () => toast.success("Language routing saved", languageRoutingText(next).label),
                  onError: (e) =>
                    toast.error("Could not save", e instanceof ApiError ? e.message : "Please try again."),
                },
              )
            }
          >
            <SelectTrigger id="campaign-language-routing" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_ROUTING.map((value) => (
                <SelectItem key={value} value={value} description={languageRoutingText(value).description}>
                  {languageRoutingText(value).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </CardBody>
    </Card>
  );
}

interface LineForm extends LineValues {
  purpose: string;
  name: string;
}

function whole([min, max]: readonly [number, number], unit = "seconds") {
  return z
    .number({ error: `Enter a number of ${unit}` })
    .int(`Whole ${unit} only`)
    .min(min, `At least ${min} ${unit}`)
    .max(max, `At most ${max.toLocaleString()} ${unit}`);
}

const media = z.string().refine((v) => mediaProblem(v) === null, "Choose a file from the audio store");

const lineSchema = z.object({
  purpose: z.string(),
  // `_LINE_NAME_RE` in core-api's schemas. Empty lets core-api name it `<slug>-<purpose>`.
  name: z
    .string()
    .regex(/^([a-z0-9][a-z0-9_-]{0,62})?$/, "Lowercase letters, digits, underscore and hyphen, up to 63"),
  description: z.string().max(200, "At most 200 characters"),
  ring_timeout_seconds: whole(LINE_BOUNDS.ring_timeout_seconds),
  ring_strategy: z.string(),
  skill_strategy: z.string(),
  previous_rep_first: z.boolean(),
  wrap_up_seconds: whole(LINE_BOUNDS.wrap_up_seconds),
  disposition_seconds: whole(LINE_BOUNDS.disposition_seconds),
  max_wait_seconds: whole(LINE_BOUNDS.max_wait_seconds),
  default_priority: whole(LINE_BOUNDS.default_priority, "points"),
  entry_announcement: media,
  moh: media,
  enabled: z.boolean(),
  // Each rule says what is wrong with it beside itself; this only keeps a broken one from saving.
  stage_rules: z
    .array(
      z.object({
        stage: z.string(),
        mode: z.enum(["only", "first"]),
        skills: z.array(z.object({ slug: z.string(), min_level: z.number() })),
        fallback_seconds: z.number().nullable(),
      }),
    )
    .refine((rules) => rules.every((r) => stageRuleProblem(r) === null), "Fix the stage rules above."),
});


/** The three things a line can play to a caller who is waiting: the switch's own music, a ringing
 *  tone (a client asked for one — a caller who hears ringing does not think they were forgotten),
 *  or a file from the audio store. The value is hold media either way, so the choice is only what
 *  is stored: "" for the switch's music, which core-api puts back as `local_stream://moh`. */
function HoldMedia({
  id, value, onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const choice = value === "" ? "music" : isTone(value) ? "tone" : "file";
  // What was picked before, so switching to Ringing tone and back does not lose the file.
  const lastFile = React.useRef(choice === "file" ? value : "");
  if (choice === "file") lastFile.current = value;

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-3">
        {HOLD_MEDIA_CHOICES.map((c) => (
          <label
            key={c.key}
            className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2 has-[:disabled]:cursor-default"
          >
            <input
              type="radio"
              name="line-hold-media"
              id={c.key === "music" ? id : undefined}
              className="mt-0.5 accent-accent-9"
              checked={choice === c.key}
              onChange={() =>
                onChange(c.key === "music" ? "" : c.key === "tone" ? RINGBACK_US : lastFile.current)
              }
            />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-text">{c.label}</span>
              <span className="block text-xs text-text-muted">{c.description}</span>
            </span>
          </label>
        ))}
      </div>
      {choice === "file" && (
        <AudioPicker
          value={value}
          onChange={onChange}
          emptyLabel="No file chosen yet"
        />
      )}
    </div>
  );
}

const HOLD_MEDIA_CHOICES: readonly { key: string; label: string; description: string }[] = [
  { key: "music", label: "Hold music", description: "The music the switch plays." },
  { key: "tone", label: "Ringing tone", description: "It rings until someone answers." },
  { key: "file", label: "A file", description: "One of your own, on a loop." },
];

function LineDialog({
  open, onOpenChange, campaign, line,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  /** Absent to add a line. */
  line?: Queue;
}) {
  const toast = useToast();
  const add = useAddLine();
  const update = useUpdateLine();
  const [formError, setFormError] = React.useState<string | null>(null);
  const firstFree = LINE_PURPOSES.find((p) => !purposeTaken(p, campaign.lines)) ?? "custom";

  const form = useForm<LineForm>({
    resolver: zodResolver(lineSchema),
    defaultValues: { ...NEW_LINE, purpose: firstFree, name: "" },
  });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      line
        ? { ...lineValues(line), purpose: line.purpose ?? "custom", name: line.name }
        : { ...NEW_LINE, purpose: firstFree, name: "" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line]);

  const purpose = form.watch("purpose");
  const ringStrategy = form.watch("ring_strategy");
  const skillStrategy = form.watch("skill_strategy");
  const primary = line?.purpose === "primary";

  const onSubmit = form.handleSubmit(async ({ purpose: chosen, name, ...settings }) => {
    setFormError(null);
    try {
      if (line) {
        const changes = lineChanges(settings, line) as LineSettingsInput;
        if (Object.keys(changes).length > 0) await update.mutateAsync({ id: line.id, ...changes });
        toast.success("Line saved", line.name);
      } else {
        const created = await add.mutateAsync({
          campaignId: campaign.id,
          purpose: chosen,
          name: name || null,
          ...(lineChanges(settings) as LineSettingsInput),
        });
        toast.success("Line added", created.name);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the line.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{line ? "Line settings" : "Add a line"}</DialogTitle>
          <DialogDescription>
            {line ? (
              <>
                How calls wait on <span className="font-mono">{line.name}</span>.
              </>
            ) : (
              `A second line for ${campaign.name}, for calls that need settings of their own.`
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            {!line && (
              <>
                <Field
                  label="Purpose"
                  htmlFor="line-purpose"
                  required
                  hint={linePurposeText(purpose).description}
                >
                  <Select value={purpose} onValueChange={(v) => form.setValue("purpose", v)}>
                    <SelectTrigger id="line-purpose">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINE_PURPOSES.map((p) => {
                        const taken = purposeTaken(p, campaign.lines);
                        return (
                          <SelectItem
                            key={p}
                            value={p}
                            disabled={taken}
                            description={taken ? "This campaign has one already." : linePurposeText(p).description}
                          >
                            {linePurposeText(p).label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  label="Name"
                  htmlFor="line-name"
                  error={errors.name?.message}
                  hint="What a number routes to and what reps sign in to. It cannot be changed later."
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    placeholder={`${campaign.slug}-${purpose}`}
                    invalid={Boolean(errors.name)}
                    {...form.register("name")}
                  />
                </Field>
              </>
            )}

            <Field label="Description" htmlFor="line-description" error={errors.description?.message}>
              <Input
                autoComplete="off"
                invalid={Boolean(errors.description)}
                {...form.register("description")}
              />
            </Field>

            <Field
              label="Ring"
              htmlFor="line-ring-strategy"
              hint={ringStrategyText(ringStrategy).description}
            >
              <Select
                value={ringStrategy}
                onValueChange={(v) => form.setValue("ring_strategy", v)}
              >
                <SelectTrigger id="line-ring-strategy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RING_STRATEGIES.map((s) => (
                    <SelectItem key={s} value={s} description={ringStrategyText(s).description}>
                      {ringStrategyText(s).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {ringStrategy !== "ring_all" && (
              <Field
                label="Who rings first"
                htmlFor="line-skill-strategy"
                hint={
                  skillStrategy === "longest_idle"
                    ? skillStrategyText(skillStrategy).description
                    : `${skillStrategyText(skillStrategy).description} Only the skills the call needs count: the line's, the number's, the handoff's, and Spanish for a Spanish lead. A call that needs none rings the rep free the longest.`
                }
              >
                <Select
                  value={skillStrategy}
                  onValueChange={(v) => form.setValue("skill_strategy", v)}
                >
                  <SelectTrigger id="line-skill-strategy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_STRATEGIES.map((s) => (
                      <SelectItem key={s} value={s} description={skillStrategyText(s).description}>
                        {skillStrategyText(s).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-xs font-medium text-text">Previous rep first</p>
                <p className="text-xs text-text-muted">
                  A caller who calls back rings the rep who last spoke to them first, when that rep
                  is free. Skills are not checked. If the rep is busy or away, the call is routed as
                  usual.
                </p>
              </div>
              <Switch
                checked={form.watch("previous_rep_first")}
                onCheckedChange={(v) => form.setValue("previous_rep_first", v, { shouldDirty: true })}
                aria-label="Previous rep first"
              />
            </div>

            <div className="space-y-1">
              <StageRulesField
                value={form.watch("stage_rules")}
                onChange={(rules) =>
                  form.setValue("stage_rules", rules, { shouldDirty: true, shouldValidate: true })
                }
                tenantId={campaign.tenant_id}
                enabled={open}
              />
              {errors.stage_rules?.message && (
                <p role="alert" className="text-xs text-danger-11">{errors.stage_rules.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Ring a rep for"
                htmlFor="line-ring"
                error={errors.ring_timeout_seconds?.message}
                hint={
                  ringStrategy === "ring_all"
                    ? "Seconds every phone rings before the whole line is rung again."
                    : "Seconds before the call is offered to another rep."
                }
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.ring_timeout_seconds)}
                  {...form.register("ring_timeout_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Wrap-up"
                htmlFor="line-wrap-up"
                error={errors.wrap_up_seconds?.message}
                hint="Seconds after a call before the rep is offered another. 0 for none."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.wrap_up_seconds)}
                  {...form.register("wrap_up_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Disposition window"
                htmlFor="line-disposition"
                error={errors.disposition_seconds?.message}
                hint="Seconds a rep has to choose an outcome after a call. 0 for none."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.disposition_seconds)}
                  {...form.register("disposition_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Caller waits up to"
                htmlFor="line-max-wait"
                error={errors.max_wait_seconds?.message}
                hint="Seconds before an inbound caller is hung up, or a handoff opens to any rep. 0 for no limit."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.max_wait_seconds)}
                  {...form.register("max_wait_seconds", { valueAsNumber: true })}
                />
              </Field>
            </div>

            <Field
              label="Entry announcement"
              htmlFor="line-announcement"
              error={errors.entry_announcement?.message}
              hint="Played to a caller before they wait."
            >
              <AudioPicker
                value={form.watch("entry_announcement")}
                onChange={(v) =>
                  form.setValue("entry_announcement", v, { shouldDirty: true, shouldValidate: true })
                }
                emptyLabel="No announcement"
              />
            </Field>
            <Field
              label="What a waiting caller hears"
              htmlFor="line-moh"
              error={errors.moh?.message}
              hint="Until a rep takes the call, and again whenever they are put on hold."
            >
              <HoldMedia
                id="line-moh"
                value={form.watch("moh")}
                onChange={(v) => form.setValue("moh", v, { shouldDirty: true, shouldValidate: true })}
              />
            </Field>

            {primary ? (
              <p className="text-xs text-text-muted">
                The primary line takes calls while the campaign is enabled.
              </p>
            ) : (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p className="text-xs font-medium text-text">Taking calls</p>
                  <p className="text-xs text-text-muted">A line that is off takes no new calls.</p>
                </div>
                <Switch
                  checked={form.watch("enabled")}
                  onCheckedChange={(v) => form.setValue("enabled", v, { shouldDirty: true })}
                  aria-label="Taking calls"
                />
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {line ? "Save line" : "Add line"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
